import { CSSProperties, ReactNode, useRef, useState, DragEvent, ChangeEvent } from "react";
import "./UploadArea.css";

export interface UploadAreaProps {
  onFileSelect: (file: File) => void;
  title?: string;
  hint?: string;
  buttonLabel?: string;
  dragEnabled?: boolean;
  accept?: string;
  capture?: boolean | "environment" | "user";
  color?: string;
  disabled?: boolean;
  icon?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

const DefaultDocIcon = () => (
  <svg viewBox="0 0 76 48">
    <rect x="2" y="2" width="72" height="44" rx="6" ry="6" />
    <circle cx="16" cy="22" r="7" />
    <path d="M9 38 q7 -6 14 0" fill="none" />
    <path d="M30 16 h36" />
    <path d="M30 24 h28" />
    <path d="M30 32 h32" />
  </svg>
);

export function UploadArea({
  onFileSelect,
  title = "Upload document",
  hint,
  buttonLabel = "Browse",
  dragEnabled = true,
  accept = "image/*",
  capture,
  color,
  disabled = false,
  icon,
  className,
  style,
}: UploadAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const resolvedHint = hint ?? (dragEnabled ? "Drag & drop or click to upload" : "Tap to upload");

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    e.target.value = "";
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!dragEnabled || disabled) return;
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!dragEnabled || disabled) return;
    e.preventDefault();
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!dragEnabled || disabled) return;
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!dragEnabled || disabled) return;
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileSelect(file);
  };

  const cssVars: CSSProperties = {
    ...(color ? ({ ["--ua-color-strong" as never]: color } as CSSProperties) : {}),
    ...style,
  };

  const rootClass = [
    "ua-root",
    dragOver && "ua-drag-over",
    disabled && "ua-disabled",
    className,
  ].filter(Boolean).join(" ");

  return (
    <div
      className={rootClass}
      style={cssVars}
      onClick={handleClick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="button"
      tabIndex={disabled ? -1 : 0}
    >
      <svg className="ua-border" preserveAspectRatio="none">
        <rect />
      </svg>

      <div className="ua-doc-wrap">
        <div className="ua-doc">{icon ?? <DefaultDocIcon />}</div>
        <div className="ua-shadow" />
      </div>

      <div className="ua-text-stack">
        <div className="ua-title">{title}</div>
        <div className="ua-hint">{resolvedHint}</div>
      </div>

      <button
        type="button"
        className="ua-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
        }}
        disabled={disabled}
      >
        {buttonLabel}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        {...(capture !== undefined ? { capture: capture as never } : {})}
        className="ua-input"
        onChange={handleChange}
      />
    </div>
  );
}

export default UploadArea;
